import type { Logger, LoggerRole } from '@zep/logger';

import { shouldLoadGoogleTagManager } from '@/consts/env';

type GtmMarketingEventName = keyof typeof GTM_MARKETING_EVENT_PARAM_ALLOWLIST;
type DataLayerValue = string | number | boolean;
type DataLayerEvent = {
  event: GtmMarketingEventName;
} & Record<string, DataLayerValue>;

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

const GTM_MARKETING_EVENT_PARAM_ALLOWLIST = {
  signup_complete: ['userType', 'type'],
  create_complete: ['create_type'],
  click_school_pro_inquiry: [],
  click_school_budget_inquiry: [],
} as const;

const isGtmMarketingEventName = (
  eventName: string,
): eventName is GtmMarketingEventName =>
  Object.prototype.hasOwnProperty.call(
    GTM_MARKETING_EVENT_PARAM_ALLOWLIST,
    eventName,
  );

const isDataLayerValue = (value: unknown): value is DataLayerValue =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean';

const getBrowserDataLayer = () => {
  if (typeof window === 'undefined') return undefined;

  window.dataLayer = window.dataLayer ?? [];
  return window.dataLayer;
};

export class GtmMarketingEventLogger implements Logger {
  public name = 'gtmMarketingEvent';
  public role: LoggerRole = 'tracking';
  private readonly _isEnabled: boolean;
  private readonly _getDataLayer: () => unknown[] | undefined;

  constructor({
    isEnabled = shouldLoadGoogleTagManager,
    getDataLayer = getBrowserDataLayer,
  }: {
    isEnabled?: boolean;
    getDataLayer?: () => unknown[] | undefined;
  } = {}) {
    this._isEnabled = isEnabled;
    this._getDataLayer = getDataLayer;
  }

  log(message: string, params?: Record<string, unknown>) {
    this.track(message, params);
  }

  track(message: string, params: Record<string, unknown> = {}) {
    if (!this._isEnabled || !isGtmMarketingEventName(message)) return;

    const dataLayer = this._getDataLayer();
    if (!dataLayer) return;

    const event = GTM_MARKETING_EVENT_PARAM_ALLOWLIST[
      message
    ].reduce<DataLayerEvent>(
      (acc, key) => {
        const value = params[key];
        if (isDataLayerValue(value)) {
          acc[key] = value;
        }
        return acc;
      },
      { event: message },
    );

    dataLayer.push(event);
  }
}
